import {
    AI_PROVIDERS,
    DEFAULT_MODEL_BY_PROVIDER,
    MODEL_REGISTRY,
    PROVIDER_ORDER,
    type AIProvider,
    type ModelCapability,
    type ModelConfig,
} from "@acme/util/src/constant/model";

/**
 * Plain, serializable view of a registry model for statically generated SEO
 * pages. Everything here comes from MODEL_REGISTRY, so the pages update when a
 * model is added or retired and never state a spec the registry doesn't hold.
 */
export interface ModelPageData {
    id: string;
    name: string;
    provider: AIProvider;
    providerName: string;
    description: string;
    contextWindow: number;
    maxOutputTokens: number;
    capabilities: ModelCapability[];
    reasoning: boolean;
    tier: ModelConfig['tier'];
    releaseDate: string | null;
    knowledgeCutoff: string | null;
    pricing: { input: number; output: number } | null;
}

const CAPABILITY_LABELS: Partial<Record<ModelCapability, string>> = {
    vision: 'Image input',
    reasoning: 'Reasoning',
    code: 'Coding',
    function_calling: 'Tool calling',
    json_mode: 'Structured output',
    extended_thinking: 'Extended thinking',
    streaming: 'Streaming',
};

export const capabilityLabel = (c: ModelCapability) => CAPABILITY_LABELS[c] ?? null;

const toPageData = (config: ModelConfig): ModelPageData => ({
    id: config.id,
    name: config.displayName,
    provider: config.provider,
    providerName: AI_PROVIDERS[config.provider].name,
    description: config.description,
    contextWindow: config.contextWindow,
    maxOutputTokens: config.maxOutputTokens,
    capabilities: [...config.capabilities],
    reasoning: !!config.isReasoning,
    tier: config.tier,
    releaseDate: config.releaseDate ?? null,
    knowledgeCutoff: config.knowledgeCutoff ?? null,
    pricing: config.pricing ?? null,
});

/** Current (non-deprecated) models, grouped in picker order, newest first. */
export const getActiveModelPages = (): ModelPageData[] =>
    (Object.values(MODEL_REGISTRY) as ModelConfig[])
        .filter(m => !m.isDeprecated)
        .map(toPageData)
        .sort((a, b) =>
            PROVIDER_ORDER.indexOf(a.provider) - PROVIDER_ORDER.indexOf(b.provider)
            || (b.releaseDate ?? '').localeCompare(a.releaseDate ?? ''));

export const getModelPage = (id: string): ModelPageData | null =>
    getActiveModelPages().find(m => m.id === id) ?? null;

/**
 * Head-to-head pages: every pair of the per-provider default models (the ones
 * people actually choose between). Kept to a curated set so each page is a
 * comparison someone searches for, not thousands of thin permutations.
 */
export const getComparePairs = (): [string, string][] => {
    const ids = PROVIDER_ORDER.map(p => DEFAULT_MODEL_BY_PROVIDER[p]).filter(id => !!getModelPage(id));
    const pairs: [string, string][] = [];
    for (let i = 0; i < ids.length; i++) {
        for (let j = i + 1; j < ids.length; j++) pairs.push([ids[i]!, ids[j]!]);
    }
    return pairs;
};

export const compareSlug = (a: string, b: string) => `${a}-vs-${b}`;

export const parseCompareSlug = (slug: string): [string, string] | null => {
    const parts = slug.split('-vs-');
    return parts.length === 2 && parts[0] && parts[1] ? [parts[0], parts[1]] : null;
};

export const formatContext = (tokens: number) =>
    tokens >= 1_000_000 ? `${+(tokens / 1_000_000).toFixed(2)}M` : `${Math.round(tokens / 1000)}K`;

export const formatPrice = (p: ModelPageData['pricing']) =>
    p ? `$${p.input} / $${p.output}` : 'See provider';

export const formatMonth = (yyyyMm: string | null) => {
    if (!yyyyMm) return 'Not published';
    const [y, m] = yyyyMm.split('-');
    const d = new Date(Date.UTC(Number(y), Number(m ?? 1) - 1, 1));
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

/**
 * Plain-language differences drawn only from registry fields. Every sentence
 * is a fact the table also shows, so the copy can't drift from the specs.
 */
export const compareFindings = (a: ModelPageData, b: ModelPageData): string[] => {
    const out: string[] = [];
    if (a.contextWindow !== b.contextWindow) {
        const [big, small] = a.contextWindow > b.contextWindow ? [a, b] : [b, a];
        out.push(`${big.name} accepts a larger context window: ${formatContext(big.contextWindow)} tokens versus ${formatContext(small.contextWindow)} for ${small.name}.`);
    } else {
        out.push(`Both models accept ${formatContext(a.contextWindow)} tokens of context.`);
    }
    if (a.maxOutputTokens !== b.maxOutputTokens) {
        const [big, small] = a.maxOutputTokens > b.maxOutputTokens ? [a, b] : [b, a];
        out.push(`${big.name} can write longer replies, up to ${formatContext(big.maxOutputTokens)} output tokens against ${formatContext(small.maxOutputTokens)}.`);
    }
    if (a.releaseDate && b.releaseDate && a.releaseDate !== b.releaseDate) {
        const [newer, older] = a.releaseDate > b.releaseDate ? [a, b] : [b, a];
        out.push(`${newer.name} is the newer release (${formatMonth(newer.releaseDate)}); ${older.name} came out in ${formatMonth(older.releaseDate)}.`);
    }
    if (a.pricing && b.pricing) {
        const blended = (m: ModelPageData) => m.pricing!.input + m.pricing!.output;
        if (blended(a) !== blended(b)) {
            const [cheap, dear] = blended(a) < blended(b) ? [a, b] : [b, a];
            out.push(`At list prices ${cheap.name} is cheaper: $${cheap.pricing!.input} input and $${cheap.pricing!.output} output per million tokens, against $${dear.pricing!.input} and $${dear.pricing!.output} for ${dear.name}.`);
        }
    }
    const onlyA = a.capabilities.filter(c => !b.capabilities.includes(c)).map(capabilityLabel).filter(Boolean);
    const onlyB = b.capabilities.filter(c => !a.capabilities.includes(c)).map(capabilityLabel).filter(Boolean);
    if (onlyA.length) out.push(`${a.name} lists ${onlyA.join(', ').toLowerCase()} support that ${b.name} does not.`);
    if (onlyB.length) out.push(`${b.name} lists ${onlyB.join(', ').toLowerCase()} support that ${a.name} does not.`);
    return out;
};
